import {
  DigitalQuestion,
} from './digitalQuestionTypes';
import { DigitalGroundTruth } from './digitalGroundtruthTypes';
import { DigitalSubmission, DigitalSubmissionAggregation } from './digitalSubmissionTypes';

export interface DigitalSurvey {
  id: number;
  name: string;
  trialId: number;
  exercise?: boolean;
  time?: number;
  allowMultipleSectionSubmission?: boolean;
  passScore: number;
  maxScore?: number;
}

export interface DigitalCase {
  id: number;
  name: string;
  url?: string;
  digitalSectionId: number;
  digitalSurveyId: number;
  sortIndex: number;
}

export interface DigitalSection {
  id: number;
  name: string;
  digitalSurveyId: number;
  sortIndex: number;
  passScore?: number;
  optional?: boolean;
  maxScore?: number;
  canOptOut?: boolean;
  referenceLinks?: {
    label: string;
    link: string;
  }[];
}

export type ExtendedDigitalSurvey = DigitalSurvey & {
  digitalSections: DigitalSection[];
  digitalQuestions: DigitalQuestion[];
  digitalCases?: DigitalCase[];
  questionCaseSettings?: QuestionCaseSetting[];
}

export type AnswerMeta = {
  digitalQuestionId: number;
  digitalCaseId: number;
  score: number;
  status: 'correct' | 'acceptable' | 'wrong';
}

export type DigitalSubmissionMeta = {
  id: number;
  enrollmentId: number;
  selectedSectionId: number;
  submissionFinalized?: boolean;
  endTime?: string; // ISO date string
  checkpoint?: string; // ISO date string
  nextCheckpoint?: string; // ISO date string
  paused?: boolean;
  acquiredScore?: number;
  reviewScore?: number;
  ctxQuestionSubmitted?: boolean;
  reviewComment?: string;
  passed?: boolean;
  isOptOut?: boolean;
}

export type SurveySubmissionMeta = {
  id: number;
  finalized: boolean;
  acquiredScore: number;
  reviewScore?: number;
  enrollmentId: number;
  trialId: number;
  reviewComment?: string;
  passed?: boolean;
}

export type AggregatedSubmissionData = {
  [secId: number]: DigitalSubmissionAggregation[];
};

export type AggregatedSubmission = {
  id: number;
  released: boolean;
  payload: AggregatedSubmissionData;
  generatedAt: string; // Date
  trialId: number;
}

export type OverviewSurveySubmission = {
  successRate: number,
  failedRate: number,
  success: number,
  failed: number,
  finalized: number,
  enrollment: number,
  sectionsSummary: {
    [secId: string]: {
      pass: number,
      fail: number,
      total: number,
      finalized: number,
    }
  }
};

export interface QuestionCaseSetting {
  id: number;
  digitalSurveyId: number;
  digitalSectionId: number;
  digitalQuestionId: number;
  digitalCaseId: number;
  compulsory?: boolean;
  hide?: boolean;
}

export interface DigitalGroundTruthGroup {
  id: number;
  index: number;
  digitalSurveyId: number;
}

export interface DigitalSectionScore {
  id: number;
  digitalSurveyId: number;
  digitalSectionId: number;
  digitalGroundTruthGroupId: number;
  passScore: number;
  maxScore: number;
}

export interface DigitalSurveyScore {
  id: number;
  digitalSurveyId: number;
  digitalGroundTruthGroupId: number;
  passScore: number;
  maxScore: number;
}

export interface DigitalQuestionScore {
  id: number;
  digitalSurveyId: number;
  digitalSectionId: number;
  digitalQuestionId: number;
  digitalGroundTruthGroupId: number;
  passScore: number;
  maxScore: number;
}

/**
 * DRT: Digital Readout Test (Digital Trial)
 * DRT Redux State
 * */
export interface SurveyDataState {
  survey?: DigitalSurvey;
  digitalSections: {
    allIds: number[];
    byId: Record<number, DigitalSection>;
    selectedSection?: number;
  };
  digitalCases: {
    allIds: number[];
    byId: Record<number, DigitalCase>;
    selectedCase?: number;
  };
  digitalQuestions: {
    allIds: number[];
    byId: Record<number, DigitalQuestion>;
  },
  digitalGroundTruth: {
    allIds: number[];
    byId: Record<number, DigitalGroundTruth>;
    selectedGroup?: number;
  }
  digitalSubmission: {
    allIds: number[];
    byId: Record<number, DigitalSubmission>;
  }
  digitalSubmissionMeta: {
    allIds: number[];
    byId: Record<number, DigitalSubmissionMeta>;
    selectedSubmissionMeta?: number;
  };
  surveySubmissionMeta?: SurveySubmissionMeta;

  questionCaseSetting: {
    allIds: number[];
    byId: Record<number, QuestionCaseSetting>;
  };
  digitalGroundTruthGroup: {
    allIds: number[];
    byId: Record<number, DigitalGroundTruthGroup>;
    selectedGroundTruthGroup: number | null; // null is the default if there is no groundtruth group
  };
  digitalSectionScore: {
    allIds?: number[];
    byId?: Record<number, DigitalSectionScore>;
  };
  digitalSurveyScore: {
    allIds?: number[];
    byId?: Record<number, DigitalSurveyScore>;
  };
  digitalQuestionScore: {
    allIds?: number[];
    byId?: Record<number, DigitalQuestionScore>;
  }
}

/** Redux Action Constants */
export const SET_DIGITAL_SURVEY = 'SET_DIGITAL_SURVEY';
export const RESET_DIGITAL_SURVEY = 'RESET_DIGITAL_SURVEY';
export const SET_DIGITAL_SECTIONS = 'SET_DIGITAL_SECTIONS';
export const ADD_DIGITAL_SECTION = 'ADD_DIGITAL_SECTION';
export const DELETE_DIGITAL_SECTION = 'DELETE_DIGITAL_SECTION';
export const SET_SELECTED_DIGITAL_SECTION = 'SET_SELECTED_DIGITAL_SECTION';
export const UPDATE_DIGITAL_SECTION = 'UPDATE_DIGITAL_SECTION';
export const SET_DIGITAL_CASES = 'SET_DIGITAL_CASES';
export const SET_SELECTED_CASE = 'SET_SELECTED_CASE';
export const ADD_DIGITAL_CASE = 'ADD_DIGITAL_CASE';
export const DELETE_DIGITAL_CASE = 'DELETE_DIGITAL_CASE';
export const UPDATE_DIGITAL_CASE = 'UPDATE_DIGITAL_CASE';
export const SET_DIGITAL_QUESTIONS = 'SET_DIGITAL_QUESTIONS';
export const SET_DIGITAL_GROUNDTRUTHS = 'SET_DIGITAL_GROUNDTRUTHS';
export const ADD_UPDATE_DIGITAL_GROUNDTRUTH = 'ADD_UPDATE_DIGITAL_GROUNDTRUTH';
export const DELETE_DIGITAL_GROUNDTRUTH = 'DELETE_DIGITAL_GROUNDTRUTH';
export const SET_DIGITAL_SUBMISSIONS = 'SET_DIGITAL_SUBMISSION';
export const ADD_UPDATE_DIGITAL_SUBMISSION = 'ADD_UPDATE_DIGITAL_SUBMISSION';
export const SET_DIGITAL_SUBMISSION_META = 'SET_DIGITAL_SUBMISSION_META';
export const UPDATE_DIGITAL_SUBMISSION_META = 'UPDATE_DIGITAL_SUBMISSION_META';
export const SET_SELECTED_DIGITAL_SUBMISSION_META = 'SET_SELECTED_DIGITAL_SUBMISSION_META';
export const SET_SURVEY_SUBMISSION_META = 'SET_SURVEY_SUBMISSION_META';
export const SET_SHOW_CONTEXT_QUESTION = 'SET_SHOW_CONTEXT_QUESTION';
export const ADD_QUESTION_CASE_SETTING = 'ADD_QUESTION_CASE_SETTING';
export const SET_QUESTION_CASE_SETTING = 'SET_QUESTION_CASE_SETTING';
export const UPDATE_QUESTION_CASE_SETTING = 'UPDATE_QUESTION_CASE_SETTING';
export const ADD_DIGITAL_GROUND_TRUTH_GROUP = 'ADD_DIGITAL_GROUND_TRUTH_GROUP';
// export const UPDATE_DIGITAL_GROUND_TRUTH_GROUP = 'UPDATE_DIGITAL_GROUND_TRUTH_GROUP';
export const SET_DIGITAL_GROUND_TRUTH_GROUP = 'SET_DIGITAL_GROUND_TRUTH_GROUP';
export const DELETE_DIGITAL_GROUND_TRUTH_GROUP = 'DELETE_DIGITAL_GROUND_TRUTH_GROUP';
export const SET_SELECTED_DIGITAL_GROUND_TRUTH_GROUP = 'SET_SELECTED_DIGITAL_GROUND_TRUTH_GROUP';
export const SET_DIGITAL_SECTION_SCORE = 'SET_DIGITAL_SECTION_SCORE';
export const CLEAR_DIGITAL_SECTION_SCORE = 'CLEAR_DIGITAL_SECTION_SCORE';
export const SET_DIGITAL_SURVEY_SCORE = 'SET_DIGITAL_SURVEY_SCORE';
export const CLEAR_DIGITAL_SURVEY_SCORE = 'CLEAR_DIGITAL_SURVEY_SCORE';
export const SET_DIGITAL_QUESTION_SCORE = 'SET_DIGITAL_QUESTION_SCORE';
export const CLEAR_DIGITAL_QUESTION_SCORE = 'CLEAR_DIGITAL_QUESTION_SCORE';

interface AddDigitalSectionAction {
  type: typeof ADD_DIGITAL_SECTION;
  payload: DigitalSection;
}

interface DeleteDigitalSectionAction {
  type: typeof DELETE_DIGITAL_SECTION;
  payload: number;
}

interface SetDigitalSurveyAction {
  type: typeof SET_DIGITAL_SURVEY;
  payload: DigitalSurvey;
}

interface ResetDigitalSurveyAction {
  type: typeof RESET_DIGITAL_SURVEY;
}

interface SetDigitalSectionsAction {
  type: typeof SET_DIGITAL_SECTIONS;
  payload: DigitalSection[];
}

interface SetSelectedDigitalSectionAction {
  type: typeof SET_SELECTED_DIGITAL_SECTION;
  payload?: number;
}

interface UpdateDigitalSectionAction {
  type: typeof UPDATE_DIGITAL_SECTION;
  payload: DigitalSection;
}

interface SetDigitalCasesAction {
  type: typeof SET_DIGITAL_CASES;
  payload: DigitalCase[];
}

interface SetSelectedCaseAction {
  type: typeof SET_SELECTED_CASE;
  payload?: number;
}

interface AddDigitalCaseAction {
  type: typeof ADD_DIGITAL_CASE;
  payload: DigitalCase;
}

interface UpdateDigitalCaseAction {
  type: typeof UPDATE_DIGITAL_CASE;
  payload: DigitalCase;
}

interface DeleteDigitalCaseAction {
  type: typeof DELETE_DIGITAL_CASE;
  payload: number;
}

interface FetchDigitalQuestionsAction {
  type: typeof SET_DIGITAL_QUESTIONS;
  payload: DigitalQuestion[];
}

interface SetDigitalGroundTruthsAction {
  type: typeof SET_DIGITAL_GROUNDTRUTHS;
  payload: DigitalGroundTruth[];
}

interface AddOrUpdateDigitalGroundtruthAction {
  type: typeof ADD_UPDATE_DIGITAL_GROUNDTRUTH;
  payload: DigitalGroundTruth;
}

interface DeleteDigitalGroundTruthAction {
  type: typeof DELETE_DIGITAL_GROUNDTRUTH;
  payload: number;
}

interface SetDigitalSubmissionsAction {
  type: typeof SET_DIGITAL_SUBMISSIONS;
  payload: DigitalSubmission[];
}

interface AddOrUpdateDigitalSubmission {
  type: typeof ADD_UPDATE_DIGITAL_SUBMISSION;
  payload: DigitalSubmission;
}

interface SetDigitalSubmissionMeta {
  type: typeof SET_DIGITAL_SUBMISSION_META;
  payload: DigitalSubmissionMeta[];
}

interface updateDigitalSubmissionMetaAction {
  type: typeof UPDATE_DIGITAL_SUBMISSION_META;
  payload: DigitalSubmissionMeta;
}

interface setSelectedDigitalSubmissionMetaAction {
  type: typeof SET_SELECTED_DIGITAL_SUBMISSION_META;
  payload?: number;
}

interface setSurveySubmissionMetaAction {
  type: typeof SET_SURVEY_SUBMISSION_META;
  payload?: SurveySubmissionMeta;
}

interface addQuestionCaseSettingAction {
  type: typeof ADD_QUESTION_CASE_SETTING;
  payload: QuestionCaseSetting;
}

interface setQuestionCaseSettingAction {
  type: typeof SET_QUESTION_CASE_SETTING;
  payload: QuestionCaseSetting[];
}

interface updateQuestionCaseSettingAction {
  type: typeof UPDATE_QUESTION_CASE_SETTING;
  payload: QuestionCaseSetting;
}

interface addDigitalGroundTruthGroupAction {
  type: typeof ADD_DIGITAL_GROUND_TRUTH_GROUP;
  payload: DigitalGroundTruthGroup;
}

// interface updateDigitalGroundTruthGroupAction {
//   type: typeof UPDATE_DIGITAL_GROUND_TRUTH_GROUP;
//   payload: DigitalGroundTruthGroup;
// }

interface deleteDigitalGroundTruthGroupAction {
  type: typeof DELETE_DIGITAL_GROUND_TRUTH_GROUP;
  payload: number;
}

interface setDigitalGroundTruthGroupAction {
  type: typeof SET_DIGITAL_GROUND_TRUTH_GROUP;
  payload: DigitalGroundTruthGroup[];
}

interface setSelectedDigitalGroundTruthGroupAction {
  type: typeof SET_SELECTED_DIGITAL_GROUND_TRUTH_GROUP;
  payload: number | null;
}

interface setDigitalSectionScoreAction {
  type: typeof SET_DIGITAL_SECTION_SCORE;
  payload: DigitalSectionScore[];
}

interface clearDigitalSectionScoresAction {
  type: typeof CLEAR_DIGITAL_SECTION_SCORE;
}

interface setDigitalSurveyScoreAction {
  type: typeof SET_DIGITAL_SURVEY_SCORE;
  payload: DigitalSurveyScore[];
}

interface clearDigitalSurveyScoreAction {
  type: typeof CLEAR_DIGITAL_SURVEY_SCORE;
}

interface setDigitalQuestionScoreAction {
  type: typeof SET_DIGITAL_QUESTION_SCORE;
  payload: DigitalQuestionScore[];
}

interface clearDigitalQuestionScoreAction {
  type: typeof CLEAR_DIGITAL_QUESTION_SCORE;
}

export type DRTActionTypes = AddDigitalSectionAction | ResetDigitalSurveyAction |
  SetDigitalCasesAction | AddDigitalCaseAction | DeleteDigitalCaseAction |
  UpdateDigitalCaseAction | SetDigitalSectionsAction | UpdateDigitalSectionAction |
  SetDigitalSurveyAction | SetSelectedDigitalSectionAction |
  DeleteDigitalSectionAction | SetSelectedCaseAction | FetchDigitalQuestionsAction |
  SetDigitalGroundTruthsAction | AddOrUpdateDigitalGroundtruthAction |
  SetDigitalSubmissionsAction | AddOrUpdateDigitalSubmission |
  SetDigitalSubmissionMeta | updateDigitalSubmissionMetaAction |
  setSelectedDigitalSubmissionMetaAction | setSurveySubmissionMetaAction |
  DeleteDigitalGroundTruthAction |
  addQuestionCaseSettingAction | setQuestionCaseSettingAction | updateQuestionCaseSettingAction |
  addDigitalGroundTruthGroupAction | deleteDigitalGroundTruthGroupAction |
  setDigitalGroundTruthGroupAction | setSelectedDigitalGroundTruthGroupAction |
  setDigitalSectionScoreAction | setDigitalSurveyScoreAction |
  clearDigitalSectionScoresAction | clearDigitalSurveyScoreAction |
  setDigitalQuestionScoreAction | clearDigitalQuestionScoreAction;
